import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { ProductImage } from './ProductImage.js';
import type {
  ArticleSearchHit,
  Paginated,
  QuickFilterTag,
  SelectedPosition,
} from '../api/types.js';

const SEITE = 10;

/**
 * Freitextsuche, der zweite Einstieg.
 *
 * Bewusst nachgeordnet: Wer den Artikel schon kennt, ist hier schneller; wer ihn
 * sucht, kommt ueber die gefuehrte Auswahl sicherer ans Ziel. Auch hier stehen
 * keine Preise -- die Trefferliste ist eine Artikelliste.
 */
export function FreeTextSearch({
  onTakeOver,
}: {
  onTakeOver: (positions: readonly SelectedPosition[]) => void;
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<Paginated<ArticleSearchHit> | null>(null);
  const [offset, setOffset] = useState(0);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [quickFilters, setQuickFilters] = useState<readonly QuickFilterTag[]>([]);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Schnellfilter kommen ausschliesslich aus der Admin-Pflege, nie fest codiert --
  // eine neue Kachel braucht keine Codeaenderung.
  useEffect(() => {
    api
      .get<{ items: QuickFilterTag[] }>('/api/catalog/search-tags')
      .then((r) => setQuickFilters(r.items))
      .catch(() => setQuickFilters([]));
  }, []);

  async function run(
    nextOffset: number,
    tagKey: string | null = selectedTag,
  ): Promise<void> {
    if (query.trim() === '' && tagKey === null) {
      // Weder Text noch Hashtag: nichts zu suchen. Die vorige Trefferliste raeumt
      // sich mit ab -- sonst bliebe sie nach dem Abwaehlen eines Hashtags stehen,
      // obwohl kein Filter mehr aktiv ist.
      setResult(null);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        limit: String(SEITE),
        offset: String(nextOffset),
      });
      if (tagKey !== null) params.set('tagKey', tagKey);
      const r = await api.get<Paginated<ArticleSearchHit>>(
        `/api/catalog/search?${params}`,
      );
      setResult(r);
      setOffset(nextOffset);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function hashtagKlick(tag: QuickFilterTag): void {
    const naechster = selectedTag === tag.key ? null : tag.key;
    setSelectedTag(naechster);
    void run(0, naechster);
  }

  /**
   * Uebernahme aus der Trefferliste.
   *
   * Geht nicht ueber /catalog/selection: Dieser Endpunkt loest eine *Variante* plus
   * Werkstoff zu einem Artikel auf. Hier ist der Artikel bereits eindeutig bekannt,
   * eine Aufloesung waere ein Umweg mit Rateanteil.
   */
  function takeOver(): void {
    const positions: SelectedPosition[] = [];
    for (const hit of result?.items ?? []) {
      const raw = (quantities[hit.articleId] ?? '').trim();
      if (raw === '') continue;
      const menge = Number(raw);
      if (!Number.isFinite(menge) || menge <= 0) continue;
      positions.push({
        articleId: hit.articleId,
        articleNumber: hit.articleNumber,
        quantity: String(menge),
      });
    }
    if (positions.length === 0) return;
    onTakeOver(positions);
    setQuantities({});
  }

  const eingegeben = Object.values(quantities).filter((v) => v.trim() !== '').length;

  return (
    <section className="card">
      <h3>Freitextsuche</h3>
      {quickFilters.length > 0 && (
        <div className="quick-filters">
          {quickFilters.map((tag) => (
            <button
              key={tag.key}
              type="button"
              className={selectedTag === tag.key ? 'hashtag-btn selected' : 'hashtag-btn'}
              title={tag.description ?? undefined}
              onClick={() => hashtagKlick(tag)}
            >
              #{tag.displayName}
            </button>
          ))}
        </div>
      )}
      <form
        className="searchbar"
        onSubmit={(e) => {
          e.preventDefault();
          void run(0);
        }}
      >
        <label htmlFor="q" className="visually-hidden">
          Suchbegriff oder Artikelnummer
        </label>
        <input
          id="q"
          type="search"
          placeholder="Artikelnummer, Werkstoff oder Nennweite, z. B. 0204002 oder EPDM DN 100"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button type="submit" className="primary" disabled={busy}>
          Suchen
        </button>
      </form>

      {error !== null && <p role="alert">{error}</p>}

      {result !== null && (
        <>
          <p className="note">
            {result.total} Treffer
            {result.total > SEITE &&
              ` · angezeigt ${offset + 1} bis ${Math.min(offset + SEITE, result.total)}`}
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">
                    <span className="visually-hidden">Darstellung</span>
                  </th>
                  <th scope="col">Artikelnummer</th>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col">Werkstoff</th>
                  <th scope="col">Nennweite</th>
                  <th scope="col">Menge</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((hit) => (
                  <tr key={hit.articleId}>
                    <td className="thumb-zelle">
                      <ProductImage
                        imageRef={hit.imageRef}
                        variant="thumbnail"
                        label={hit.productLabel}
                      />
                    </td>
                    <th scope="row">
                      <code>{hit.articleNumber}</code>
                    </th>
                    <td>{hit.description}</td>
                    <td>{hit.materialLabel}</td>
                    <td>{hit.sizeLabel ?? '–'}</td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="qty"
                        aria-label={`Menge ${hit.articleNumber}`}
                        value={quantities[hit.articleId] ?? ''}
                        onChange={(e) =>
                          setQuantities((prev) => ({
                            ...prev,
                            [hit.articleId]: e.target.value,
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row">
            <button
              type="button"
              disabled={offset === 0 || busy}
              onClick={() => void run(Math.max(0, offset - SEITE))}
            >
              Zurueck
            </button>
            <button
              type="button"
              disabled={offset + SEITE >= result.total || busy}
              onClick={() => void run(offset + SEITE)}
            >
              Weiter
            </button>
            <button
              type="button"
              className="primary"
              disabled={eingegeben === 0}
              onClick={takeOver}
            >
              Auswahl uebernehmen
              {eingegeben > 0 && ` (${eingegeben})`}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
